import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  UpdateProfileRequest,
  GOTRA_PRESETS,
  type UpdateProfileRequest as UpdateProfileRequestType,
} from "@vedaunion/shared";
import { AuthInput } from "../../components/auth/AuthInput";
import { useMe, useUpdateProfile } from "../../lib/auth";
import { roleLabel } from "../../lib/roles";
import { usePreferences } from "../../lib/preferences";
import { ThemeToggle } from "../../components/ui/ThemeToggle";
import { ChantSettings } from "../../components/chant/ChantSettings";
import type { DateFormatPreference, TimeFormatPreference } from "@vedaunion/shared";

const DATE_FORMAT_LABEL: Record<DateFormatPreference, string> = {
  auto: "Automatic (locale)",
  dmy: "Day Month Year — 5 July 2026",
  mdy: "Month Day, Year — July 5, 2026",
  ymd: "Year Month Day — 2026 July 5",
  iso: "ISO — 2026-07-05",
};
const TIME_FORMAT_LABEL: Record<TimeFormatPreference, string> = {
  auto: "Automatic (locale)",
  h12: "12-hour — 3:30 PM",
  h24: "24-hour — 15:30",
};

export default function DashboardProfile() {
  const { data } = useMe();
  const user = data?.user;
  const { prefs, update: updatePrefs } = usePreferences();
  const { mutateAsync, isPending } = useUpdateProfile();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UpdateProfileRequestType>({
    resolver: zodResolver(UpdateProfileRequest),
    defaultValues: {
      displayName: user?.displayName ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      dateOfBirth: user?.dateOfBirth ?? "",
      country: user?.country ?? "",
      preferredLanguage: user?.preferredLanguage ?? "",
      gotra: user?.gotra ?? "",
    },
  });

  // Once the user query resolves (or changes), seed the form with current values.
  useEffect(() => {
    if (!user) return;
    reset({
      displayName: user.displayName,
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      dateOfBirth: user.dateOfBirth ?? "",
      country: user.country ?? "",
      preferredLanguage: user.preferredLanguage ?? "",
      gotra: user.gotra ?? "",
    });
  }, [user, reset]);

  if (!user) return null;

  const onSubmit = async (values: UpdateProfileRequestType) => {
    // Coerce empty strings to null so we explicitly clear fields server-side.
    const body: UpdateProfileRequestType = {
      displayName: values.displayName?.trim() || undefined,
      firstName: values.firstName?.toString().trim() || null,
      lastName: values.lastName?.toString().trim() || null,
      dateOfBirth: values.dateOfBirth?.toString().trim() || null,
      country: values.country?.toString().trim().toUpperCase() || null,
      preferredLanguage: values.preferredLanguage?.toString().trim().toLowerCase() || null,
      gotra: values.gotra?.toString().trim() || null,
    };
    await mutateAsync(body);
    setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 4000);
  };

  return (
    <div className="container-edit">
      <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12 max-w-[1100px]">
        <div className="col-span-12 lg:col-span-8">
          <p className="page-eyebrow">Profile</p>
          <h1 className="page-title mb-6">Your details.</h1>

          {savedAt && (
            <p className="auth-banner mb-5">
              <span style={{ color: "var(--color-violet)" }}>Saved.</span>{" "}
              Changes are live.
            </p>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="auth-form-stack">
            <AuthInput
              label="Display name"
              autoComplete="name"
              {...register("displayName")}
              error={errors.displayName?.message}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <AuthInput
                label="First name"
                autoComplete="given-name"
                {...register("firstName")}
                error={errors.firstName?.message}
              />
              <AuthInput
                label="Last name"
                autoComplete="family-name"
                {...register("lastName")}
                error={errors.lastName?.message}
              />
            </div>
            <AuthInput
              label="Date of birth"
              type="date"
              autoComplete="bday"
              {...register("dateOfBirth")}
              error={errors.dateOfBirth?.message}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <AuthInput
                label="Country (2-letter code)"
                autoComplete="country"
                placeholder="HR, SI, US…"
                maxLength={2}
                {...register("country")}
                error={errors.country?.message}
              />
              <AuthInput
                label="Preferred language"
                placeholder="en, hr…"
                maxLength={8}
                {...register("preferredLanguage")}
                error={errors.preferredLanguage?.message}
              />
            </div>

            <div className="sk-field">
              <span className="sk-label">Gotra</span>
              <select
                className="sk-input"
                value={GOTRA_PRESETS.some((g) => g.value === (watch("gotra") ?? "")) ? (watch("gotra") ?? "") : "other"}
                onChange={(e) =>
                  setValue("gotra", e.target.value === "other" ? "" : e.target.value, { shouldDirty: true })
                }
              >
                {GOTRA_PRESETS.map((g) => (
                  <option key={g.value} value={g.value}>{g.note ? `${g.label} — ${g.note}` : g.label}</option>
                ))}
                <option value="other">Other…</option>
              </select>
              {!GOTRA_PRESETS.some((g) => g.value === (watch("gotra") ?? "")) && (
                <input
                  className="sk-input"
                  style={{ marginTop: "0.5rem" }}
                  placeholder="your gotra (IAST)"
                  {...register("gotra")}
                />
              )}
              <p className="sk-note">
                Your preferred gotra — used as the default in your saṅkalpa. Gotra is
                patrilineal (it can’t be derived from a birth date); if you don’t know
                yours, the traditional default is <em>kāśyapa</em>.
              </p>
            </div>

            <div className="auth-actions">
              <button
                type="submit"
                className="btn-app"
                disabled={isPending || !isDirty}
              >
                {isPending ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>

          {/* Display preferences — saved instantly (account when signed in). */}
          <section className="mt-12 pt-9 border-t border-[var(--color-rule)]">
            <p className="page-eyebrow">Preferences</p>
            <h2 className="font-display text-[1.6rem] leading-[1.15] tracking-tight mb-6">Display</h2>

            <div className="sk-field">
              <span className="sk-label">Colour theme</span>
              <div className="mt-1">
                <ThemeToggle variant="segmented" />
              </div>
              <p className="sk-note">Light, dark, or follow your device. Applies across the site.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-6">
              <label className="sk-field">
                <span className="sk-label">Date format</span>
                <select
                  className="sk-input"
                  value={prefs.dateFormat}
                  onChange={(e) => updatePrefs({ dateFormat: e.target.value as DateFormatPreference })}
                >
                  {(Object.keys(DATE_FORMAT_LABEL) as DateFormatPreference[]).map((k) => (
                    <option key={k} value={k}>{DATE_FORMAT_LABEL[k]}</option>
                  ))}
                </select>
              </label>
              <label className="sk-field">
                <span className="sk-label">Time format</span>
                <select
                  className="sk-input"
                  value={prefs.timeFormat}
                  onChange={(e) => updatePrefs({ timeFormat: e.target.value as TimeFormatPreference })}
                >
                  {(Object.keys(TIME_FORMAT_LABEL) as TimeFormatPreference[]).map((k) => (
                    <option key={k} value={k}>{TIME_FORMAT_LABEL[k]}</option>
                  ))}
                </select>
              </label>
            </div>
            <p className="sk-note mt-4">
              {user ? "Saved to your account." : "Saved on this device."}
            </p>

            {/* Chant reading defaults — the embeddable chant reader inherits
                these; each reader can still be tweaked in place. */}
            <div className="mt-9 pt-8 border-t border-[var(--color-rule)]">
              <h3 className="font-display text-[1.3rem] leading-[1.15] tracking-tight mb-1">Chant reading</h3>
              <p className="sk-note mb-5" style={{ marginTop: 0 }}>
                Defaults for every chant reader on the site. You can still adjust an individual reading from its settings.
              </p>
              <ChantSettings value={prefs.chant} onChange={(patch) => updatePrefs({ chant: patch })} />
            </div>
          </section>
        </div>

        <aside className="col-span-12 lg:col-span-4">
          <div className="veda-panel">
            <p className="font-grotesk text-[0.7rem] tracking-[0.22em] uppercase text-[var(--color-violet)] mb-5">
              Account
            </p>
            <dl className="space-y-5 font-grotesk text-[0.85rem]">
              <div>
                <dt className="text-[0.65rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)] mb-1">
                  Email
                </dt>
                <dd className="font-display text-[1.05rem] text-[var(--color-ink)] break-all">
                  {user.email}
                </dd>
              </div>
              <div>
                <dt className="text-[0.65rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)] mb-1">
                  Role
                </dt>
                <dd className="font-display text-[1.05rem] text-[var(--color-ink)]">
                  {roleLabel(user.role)}
                </dd>
              </div>
              <div>
                <dt className="text-[0.65rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)] mb-1">
                  Member since
                </dt>
                <dd className="font-display text-[1.05rem] text-[var(--color-ink)]">
                  {new Date(user.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </dd>
              </div>
            </dl>
            <p className="mt-6 font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] leading-[1.5]">
              Email change requires verification — coming in a later phase.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
